/**
 * Tiny, user-scoped snapshot of the last known profile state.
 *
 * Purpose: bridge the gap on a cold start / slow network so the user sees
 * their own name and their real onboarding progress immediately instead of
 * the generic "Hay friend!" greeting and a false "0 of 6" checklist.
 *
 * This is presentation-only caching. It is always replaced by fresh data as
 * soon as the network responds, and it is scoped by user id so one account
 * can never read another account's snapshot.
 */

const KEY_PREFIX = "wc_profile_snapshot_";

export interface ProfileSnapshot {
  firstName?: string | null;
  onboardingCompletedCount?: number | null;
  savedAt?: number;
}

const keyFor = (userId: string) => `${KEY_PREFIX}${userId}`;

export function readProfileSnapshot(userId: string | undefined | null): ProfileSnapshot | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as ProfileSnapshot;
  } catch {
    return null;
  }
}

export function writeProfileSnapshot(
  userId: string | undefined | null,
  patch: ProfileSnapshot
): void {
  if (!userId) return;
  try {
    const current = readProfileSnapshot(userId) ?? {};
    const next: ProfileSnapshot = { ...current, ...patch, savedAt: Date.now() };
    localStorage.setItem(keyFor(userId), JSON.stringify(next));
  } catch {
    // Storage full or unavailable (private mode) — snapshot is optional.
  }
}

export function clearProfileSnapshot(userId: string | undefined | null): void {
  if (!userId) return;
  try {
    localStorage.removeItem(keyFor(userId));
  } catch {
    // no-op
  }
}
